import { contentFactoryMap } from "../constants";

const en = {
  name: "English",
  code: "en",
  direction: "ltr",
  pages: {
    login: {
      title: "Welcome to LTS Admin!",
      subtitle: "Please log in to continue.",
      email: "Email",
      password: "Password",
      login: "Login",
      error: "Invalid username or password",
    },
    queue: {
      title: "Queue Manager",
      inPageTitle: {
        short: "Queue",
        long: "Queue Manager",
      },
      subtitle: "Queue items status and management console",
      refresh: "Refresh",
      refreshed: "Queue refreshed",
      tabs: {
        all: "All",
        pending: "Pending",
        processing: "In Progress",
        completed: "Completed",
        failed: "Failed",
      },
      actions: {
        downloadSource: "Download source video",
        downloadFailed: "Failed to download source video",
        retry: "Retry job",
        retrySuccess: "Job re-queued",
        retryFailed: "Failed to retry job",
      },
    },
    highlights: {
      title: "Highlights",
      inPageTitle: {
        short: "Highlights",
        long: "Highlight Candidates",
      },
      subtitle:
        "Picking a moment starts its article automatically. Candidates expire after 14 days.",
      refresh: "Refresh",
      refreshed: "Highlights refreshed",
      rtlToggle: "RTL",
      copyLink: "Copy link to this candidate",
      linkCopied: "Link copied",
      backToAll: "All candidates",
      focusNotFound: {
        title: "Candidate not found",
        subtitle:
          "This link may point to a candidate that was merged or removed. Go back to see all candidates.",
      },
      titleChoice: {
        label: "Article title",
        engine: "Final",
        candidate: "Draft",
        applied: "Title updated",
      },
      picked: "Picked — article generation started",
      pickedAgain: "Picked — article already generated",
      unpicked: "Returned to pending",
      generateQueued: "Generation queued",
      merge: {
        warn: "Possible duplicate",
        of: "Same story as",
        action: "Merge",
        merging: "Merging…",
        merged: "Merged into one candidate",
        loadError: "Couldn't load the suggested clip",
        mergedFrom: "Merged from",
        clips: "clips",
      },
      tabs: {
        pending: "Pending",
        picked: "Picked",
      },
      filter: {
        show: "Show",
        all: "All shows",
        noMatch: "No matching show",
        search: "Search",
        searchPlaceholder: "Title, summary, show…",
        from: "From",
        to: "To",
        clear: "Clear",
        date: {
          label: "Date",
          allTime: "All dates",
          today: "Today",
          yesterday: "Yesterday",
          last7: "Last 7 days",
          thisMonth: "This month",
          custom: "Custom range",
          apply: "Apply",
          from: "From",
          to: "To",
        },
      },
      columns: {
        status: "Status",
        show: "Show",
        title: "Title",
        startedAt: "Recorded",
        duration: "Length",
      },
      generation: {
        running: "Generating article…",
        failed: "Generation failed",
      },
      actions: {
        pick: "Pick",
        picking: "Picking…",
        unpick: "Unpick",
        retry: "Retry",
        working: "Working…",
        viewArticle: "View article",
      },
      empty: {
        pending: {
          title: "No pending candidates",
          subtitle:
            "When the highlighter finds article-worthy moments, they'll appear here.",
        },
        picked: {
          title: "Nothing picked yet",
          subtitle:
            "Picking a candidate starts its article — your picks and their articles live here.",
        },
        search: {
          title: "No matches",
          subtitle: "No candidate matches these filters on either tab.",
        },
      },
      runs: {
        title: "Recording runs",
        tooltip: "Recording run history",
        empty: "No runs recorded yet",
        clips: "clips",
        noShow: "Show not matched",
        status: {
          running: "Running",
          completed: "Completed",
          empty: "No clips",
          failed: "Failed",
        },
      },
    },
    clipToArticle: {
      title: "Clip to Article",
      subtitle:
        "Turn any part of the last 4 days of recordings into an article.",
      refresh: "Refresh",
      untitled: "Unmatched recording",
      today: "Today",
      yesterday: "Yesterday",
      back: "All shows",
      aired: "aired",
      showWindowLabel: "Show within the recording",
      playhead: "Playhead",
      start: "Start",
      end: "End",
      length: "Length",
      setStart: "Set start (I)",
      setEnd: "Set end (O)",
      setStartHint: "Move the start to where the playhead is. Shortcut: I",
      setEndHint: "Move the end to where the playhead is. Shortcut: O",
      preview: "Preview selection",
      stop: "Stop",
      reset: "Reset",
      invalidTime: "Use M:SS or H:MM:SS",
      generate: "Create article",
      confirm: {
        title: "Create an article from this clip?",
        show: "Show",
        body: "The article is written from the selected part of the recording. It takes a few minutes.",
        cancel: "Cancel",
        ok: "Yes, create it",
      },
      queued:
        "Queued — the article appears in Content in a few minutes. Track it on the Queue page.",
      tooLong:
        "The selection is {over} over the 15 minute limit. Long clips make weak articles and cost a lot to generate.",
      tooShort: "The selection is too short — it needs to be at least {min}.",
      empty: {
        title: "No shows in the last 4 days",
        subtitle: "They appear here after the next scheduled recording run.",
      },
    },
    recordings: {
      title: "Recordings",
      subtitle:
        "Saved TV recordings, kept for 14 days. Re-run the highlighter on one to get fresh candidates.",
      refresh: "Refresh",
      rerun: "Run highlighter",
      selected: "{n}/{max} selected",
      launched:
        "Started {n} highlighter runs — new candidates appear on the Highlights page in 10–20 minutes.",
      launchFailed: "{n} recordings failed to start",
      columns: {
        date: "Date",
        window: "Window",
        show: "Show",
        size: "Size",
      },
      runs: {
        label: "runs",
        title: "Run history",
        empty: "No runs recorded for this recording",
        clips: "clips",
        rerun: "Re-run",
        status: {
          running: "Running",
          completed: "Completed",
          empty: "No clips",
          failed: "Failed",
        },
      },
      empty: {
        title: "No recordings yet",
        subtitle: "They appear here after the next scheduled recording run.",
      },
    },
    home: {
      title: "Home",
      subtitle: "Here's what's happening across your workspace today.",
      greeting: {
        morning: "Good morning",
        afternoon: "Good afternoon",
        evening: "Good evening",
      },
      viewAll: "View all",
      rtlToggle: "RTL",
      time: { now: "now", m: "m", h: "h", d: "d" },
      articles: { title: "Latest articles", empty: "No articles yet" },
      recordings: {
        title: "Recording today",
        empty: "Nothing scheduled for today",
        planned: "Planned",
        recording: "Recording",
        captured: "Captured",
        failed: "Failed",
      },
      highlights: { title: "Highlights", empty: "No candidates waiting" },
      trendscout: { title: "Trending topics", empty: "No trends yet" },
      todaybefore: {
        title: "Today, years ago",
        empty: "Nothing on record for today",
        yearsAgo: "{n} years ago",
      },
      activity: {
        title: "Recent activity",
        empty: "No recent activity",
        article: "New article",
        highlight: "New highlight",
        trend: "Trend spotted",
        recording: "Recording captured",
      },
    },
    storehouse: {
      title: "Storehouse",
      subtitle: "Image Storehouse. Click to copy, double click to open",
      alert:
        "The content displayed is filtered according to your user permissions and content ownership.",
      sync: "Sync",
      upload: {
        short: "Upload",
        long: "Upload Image",
      },
      uploadDialog: {
        title: "Upload Image",
        description: "To upload a file, please select it from your device.",
        instructions: "Drag and drop images here, or click to select files",
        upload: "Upload",
        cancel: "Cancel",
      },
    },
    campaigns: {
      title: "Campaigns",
      subtitle: "Manage, create, and edit campaigns",
      alert: "This page is being developed",
    },
    automations: {
      title: "Automations",
      subtitle: "Manage, create, and edit automations",
      alert: "This page is being developed",
    },
    editor: {
      title: "Content Editor",
      subtitle: "Create and edit content",
      save: "Save",
      publish: "Publish",
      metadata: "Metadata",
      download: "Download",
      drawer: {
        title: "Metadata",
        description: "View and edit content metadata",
        form: {
          title: "Title",
          tags: "Tags",
          tagsPlaceholder: "Search tags…",
          banner: "Banner",
          description: "Description",
          preview: "Preview",
          general: "General",
        },
      },
      toaster: {
        contentId: "Content ID is missing",
        error: "An unkown error has occurred",
        length: "Content is empty",
        success: "Article saved",
        publishSuccess: "Article published",
        publishFailed: "Failed to publish",
      },
      ai: {
        title: "Editing assistant",
        close: "Close",
        newChat: "New chat",
        empty:
          "Ask for a change. Select text in the article first to point the assistant at it.",
        suggestions: [
          "Shorten the lead",
          "Fix the grammar",
          "Sharpen this paragraph",
        ],
        placeholder: "Ask for a change…",
        thinking: "Thinking",
        elapsed: "{{seconds}}s",
        hint: "Enter to send · Shift+Enter for a new line",
        send: "Send",
        change: "change",
        oneChange: "1 proposed change",
        changes: "{{count}} proposed changes",
        apply: "Apply",
        reject: "Reject",
        applyAll: "Apply all",
        rejectAll: "Reject all",
        stale: "The text this change referred to has since changed.",
        dropped:
          "{{count}} more suggestion(s) couldn't be matched to a unique spot in the article.",
        failed: "The assistant could not be reached",
      },
    },
    content: {
      title: "Content",
      subtitle: "Create and manage content",
      search: {
        placeholder: "Search by title...",
        empty: "No results found for",
      },
      pagination: {
        rowsPerPage: "Rows per page:",
        of: "of",
        nextPage: "Next page",
        previousPage: "Previous page",
      },
      filter: {
        source: "Source",
        tags: "Tags",
        tagsPlaceholder: "Filter by tags…",
        tagsEmpty: "No matching tags",
        clear: "Clear",
        empty: "No sources available",
        sources: {
          manual: "Manual",
          text: "Text",
          video2article: "Video",
          reframer: "Reframer",
          "url-rewriter": "RankLens",
          "content-creator": "Content Creator",
        },
        status: {
          label: "Status",
          all: "All",
          published: "Published",
          unpublished: "Unpublished",
        },
      },
      unpublishDialog: {
        title: "Mark as unpublished?",
        message: "Are you sure you want to mark this article as unpublished? You can re-mark it as published at any time.",
        cancel: "Cancel",
        confirm: "Confirm",
      },
      deleteDialog: {
        title: "Delete this article?",
        message: "The article will be removed from the list. Contact an administrator if you need it back.",
        cancel: "Cancel",
        confirm: "Delete",
      },
      publishedTooltip: {
        on: "Published",
        off: "Draft",
        readOnly: "Open the article to publish",
      },
      actions: {
        edit: "Edit",
        editReframe: "Edit Reframe",
        download: "Download",
        downloadRequiresPublish: "Mark as published to enable download",
        delete: "Delete",
      },
      toaster: {
        unauthorized: "Unauthorized access",
        error: "An unkown error has occurred",
        delete: "Content Deleted",
        create: "Content Created",
        deploy: "Content Deployed",
        launch: "Content Launched",
        bind: "Article added to site successfully",
        downloadUnavailable: "Download is currently unavailable for this content type",
        downloadRequiresPublish: "Mark the article as published before downloading",
      },
      new: {
        short: "New",
        long: "New Content",
      },
      popup: {
        save: "Generate",
        cancel: "Cancel",
        title: "Create Content",
        [contentFactoryMap.article]: {
          title: "Create New Article",
          articleTitle: "Title",
          success: "Content was created successfully.",
        },
        [contentFactoryMap.contentCreator]: {
          title: "Generate Content",
          prompt: "Prompt",
          success: "Content was added to the queue successfully.",
          promptHelper:
            "Provide a prompt, and the AI will generate an article for you. Remember, the more elaborate your prompt, the better the results.",
          language: "Language",
          languageHelper: "Select the output language.",
          languages: {
            auto: "Auto",
            en: "English",
            he: "Hebrew",
          },
          contentType: "Content Type",
          contentTypeHelper: "Select the content category.",
          contentTypes: {
            auto: "Auto",
            news: "News",
            entertainment: "Entertainment",
            explainer: "Explainer",
          },
          minLength: "Minimum Word Count",
          maxLength: "Maximum Word Count",
          lengthHelper: "Set the word count range.",
          maxSources: "Max Sources",
          maxSourcesHelper: "Set the research thoroughness.",
          noImages: "No Images",
          noImagesHelper: "Skip image processing.",
        },
        [contentFactoryMap.reframer]: {
          title: "Reframer",
          name: "Name",
          success: "Video was added to the reframer queue successfully.",
        },
        [contentFactoryMap.videoContentCreator]: {
          title: "Video Content Creator",
          success: "Videos were added to the generation queue successfully.",
          alert:
            "Upload one or more video files and the AI will generate a Hebrew article per video with extracted screenshots.",
          fileHelper: "Only video files are accepted (MP4, AVI, MOV, etc.)",
          uploadVideo: "Add Video",
          paragraphsLabel: "Paragraphs",
          paragraphsHelper:
            "Target number of paragraphs for each generated article.",
        },
      },
      drawer: {
        title: "New Content",
        description: "Pick the content type you want to create",
        types: [
          {
            id: contentFactoryMap.article,
            title: "Article",
            description: "Create a new article using rich text editor",
          },
          // Content Creator (prompt-to-article) is hidden until the langchain
          // dependency issue in the v2a/content-creator Python pipeline is
          // resolved — currently every job fails on `module 'langchain' has
          // no attribute 'verbose'`. Re-enable once the pipeline is fixed.
          // {
          //   id: contentFactoryMap.contentCreator,
          //   title: "Content Creator",
          //   description: "Create content by prompting an AI",
          // },
          {
            id: contentFactoryMap.videoContentCreator,
            title: "Video Content Creator",
            description: "Create content by uploading video files",
          },
          {
            id: contentFactoryMap.reframer,
            title: "Reframer",
            description: "Reframe a video to a vertical format",
          },
        ],
      },
    },
    configurations: {
      title: {
        short: "Config",
        long: "Site Configurations",
      },
      subtitle: "Edit, deploy, and customize the website ",
      description: "Site SEO Description",
      template: "Template",
      categories: "Categories",
      republish: "Republish",
      add: {
        short: "Add",
        long: "Add Article",
      },
      addCategory: "Add",
      error: "Unknown error has occurred",
      empty: "No articles binded to this site",
      delete: "Delete",
      edit: "Edit Category",
      none: "Empty",
      editMeta: "Edit Meta Files",
      success: "Online",
      failure: "Empty",
      maxFeatured: "You exceeded the maximum number of featured articles (5)",
      addQueue: "Action added to queue successfully",
      search: {
        placeholder: "Search by title...",
        empty: "No results found for",
      },
      drawer: {
        title: "Add Article",
        description: "Add a new article to the site",
        empty: "No articles to display",
      },
      fileDrawer: {
        title: "Edit",
        description:
          "Files are core components of the site, which used for verification of the site, managing ads, and SEO, and general content",
      },
      txtDrawer: {
        title: "Edit Metafiles",
        description:
          "Metafiles are sets of data that supports the site's ads, SEO, and general fill",
        mediaAlert: "Write the URL of the media file from S3 bucket",
        robots: "Robots.txt",
        ads: "Ads.txt",
        logo: "Logo.svg",
        favicon: "Favicon",
        sitemap: "Sitemap.xml",
        files: "Files",
        media: "Media",
        about: "About",
        privacy: "Privacy Policy",
        terms: "Terms of Service",
        contact: "Contact Us",

        structure: "Structure",
      },
      dialog: {
        title: "Add Category",
        cancel: "Cancel",
        save: "Save",
      },
    },
    contentNew: {
      title: "New",
      action: "Change Type",
    },
    sites: {
      title: "Sites",
      subtitle: "Manage your sites",
      alert: "This page is being developed - should be a list of sites",
    },
    stream: {
      title: "Stream",
      subtitle: "Live stream management",
      days: {
        abbr: ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"],
        today: "TODAY",
      },
      errors: {
        showAlreadyStarted: "Cannot modify a show that has already started",
        saveFailed: "Failed to save show",
        removeFailed: "Failed to remove show",
        guideUnavailable: "TV guide is currently unavailable. Please try again later.",
      },
      success: {
        saved: "Show saved successfully",
        removed: "Show removed successfully",
        recurringSaved: "Recurring recording created",
      },
      record: {
        thisAiring: "This airing",
        recordOnce: "Record this airing",
        remove: "Remove recording",
        repeatWeekly: "Repeat weekly",
        pickDays: "Pick the days to record this time slot",
        setupRepeat: "Set up repeat",
        repeatsOn: "Repeats:",
        stopRepeating: "Stop repeating",
        includeReruns: "Include reruns (ש.ח)",
        rerunConfirm: {
          title: "Record reruns too?",
          message:
            "By default, recurring recordings skip reruns (ש.ח). Confirm recording reruns as well?",
          confirm: "Yes, include",
          cancel: "Cancel",
        },
        close: "Close",
        status: {
          scheduled: "Scheduled",
          recording: "Recording",
          captured: "Recorded",
          failed: "Failed",
          recurring: "Recurring",
        },
      },
    },
    settings: {
      title: "Settings",
      subtitle: "Admin settings",
      save: "Save",
      tabs: {
        recording: "Recording",
        models: "Models",
      },
      models: {
        heading: "AI models",
        help: "Which model each feature runs on. Fallback models stay fixed — a failed call still retries on the pinned backup.",
        defaultTag: "default",
        overridden: "Changed",
        loadFailed: "Failed to load models",
        features: {
          trendscoutScan: {
            label: "TrendScout scan",
            description: "Hourly RSS filter + Hebrew titles. The highest-volume job.",
          },
          todaybefore: {
            label: "Today Before",
            description: "Hebrew translation of the on-this-day feed.",
          },
          assistant: {
            label: "Editor assistant",
            description: "The in-editor chat assistant. Runs on every turn.",
          },
          reshetJudge: {
            label: "Recurring show matcher",
            description: "Matches a renamed show against the TV guide. Once per unmatched show per day.",
          },
          v2aVision: {
            label: "Video analysis",
            description: "Watches the clip in video2article. Default for CMS uploads and highlights.",
          },
          writingEngine: {
            label: "Writing engine",
            description: "Writes the article itself (TrendScout, Today Before, video2article). Claude and DeepSeek need their API key set on the worker.",
          },
        },
      },
      recording: {
        heading: "Recording",
        padLabel: "Padding (minutes)",
        padHelp:
          "Extra minutes recorded before and after each show, on both ends. Applies to every scheduled recording.",
        clipPadLabel: "Clip padding (seconds)",
        clipPadHelp: "Lead-in / trailing per highlight clip",
        guideRefreshLabel: "Guide refresh (minutes)",
        guideRefreshHelp:
          "How often the TV guide is re-fetched and today's plan re-matched against it. Lower tracks intra-day schedule changes faster; windows already recording are never moved.",
      },
      success: { saved: "Settings saved" },
      errors: {
        invalidPadding: "Padding must be between 0 and 60 minutes",
        invalidClipPadding: "Clip padding must be between 0 and 30 seconds",
        invalidGuideRefresh: "Guide refresh must be between 1 and 720 minutes",
        saveFailed: "Failed to save settings",
      },
    },
    player: {
      title: "Video Player",
      subtitle: "Watch reframed video",
      error: "Failed to load video",
      notFound: "No video found for this content",
      editor: "Editor",
    },
    reframeEditor: {
      toolbar: {
        title: "Reframe Editor",
        back: "Back to content",
        scene: "Strategy:",
        split: "Split Scene",
        merge: "Merge",
        mergeSelected: "Merge selected",
        undo: "Undo",
        redo: "Redo",
        save: "Save & Queue Re-render",
        saving: "Queueing...",
        autoCutLabel: "Auto-cut Speakers",
        autoCut: "Cut between active speakers automatically",
        noScene: "No scene selected",
        multiSelected: "{n} scenes selected",
        clipboardChip: "Crop in clipboard",
        overlay: "Overlay",
        shortcuts: "Keyboard shortcuts",
      },
      shortcutsDialog: {
        title: "Keyboard Shortcuts",
        groups: {
          playback: "Playback",
          crop: "Crop",
          scenes: "Scenes",
          editing: "Editing",
        },
        playPause: "Play / pause",
        frameStep: "Step one frame back / forward",
        seekStart: "Jump to the start",
        seekEnd: "Jump to the end",
        nudge: "Move the crop by 1 pixel",
        nudgeFast: "Move the crop by 10 pixels",
        copyCrop: "Copy crop",
        pasteCrop: "Paste crop",
        prevNextScene: "Previous / next scene",
        split: "Split the scene at the playhead",
        mergeNext: "Merge with the next scene",
        mergePrev: "Merge with the previous scene",
        clearSelection: "Clear multi-selection",
        undo: "Undo",
        redo: "Redo",
        help: "Show this list",
      },
      overlay: {
        title: "Overlay layer",
        close: "Close",
        save: "Save overlay",
        none: "No overlay configured",
        add: "Add overlay",
        summaryTemplate: "Template",
        summaryTiming: "Timing",
        timingFull: "Full duration",
        timingFromStart: "First {n}s",
        timingRange: "From {start}s to {end}s",
      },
      strategies: {
        focus: "Focus",
        static: "Static",
        bouncing: "Bouncing",
        virtual_scene: "Virtual Scene",
        undefined: "Undefined",
        fallback: "Fallback",
      },
      preview: {
        label: "9:16 Preview",
      },
      timeline: {
        label: "Timeline",
        noScenes: "No scenes to display",
        sceneCount: "scenes",
        contextMenu: {
          splitHere: "Split at cursor",
          mergePrev: "Merge with previous",
          mergeNext: "Merge with next",
          mergeSelected: "Merge selected",
          copyCrop: "Copy crop",
          pasteCrop: "Paste crop",
          pasteCropMulti: "Paste crop to selected ({n})",
          applyCropToAll: "Apply crop to all scenes",
          resetScene: "Reset to AI default",
          undo: "Undo",
          redo: "Redo",
          shortcut: {
            split: "S",
            mergeNext: "M",
            mergePrev: "Shift+M",
            copy: "Ctrl+C",
            paste: "Ctrl+V",
            undo: "Ctrl+Z",
            redo: "Ctrl+Shift+Z",
          },
        },
      },
      toaster: {
        cropCopied: "Crop copied",
        cropPasted: "Crop pasted to {n} scene(s)",
        cropApplied: "Crop applied to all scenes",
        scenesMerged: "{n} scenes merged",
        sceneSplit: "Scene split",
        sceneReset: "Scene reset to AI default",
        clipboardEmpty: "No crop in clipboard — copy one first",
        nothingToMerge: "Select 2+ adjacent scenes to merge",
        mergeNotContiguous: "Selected scenes must be adjacent to merge",
        queued: "Re-render queued — track progress in the queue",
        overlayApplying: "Applying overlay…",
        overlayQueued: "Overlay queued — track progress in the queue",
        nothingToSave: "No scene changes and no overlay — nothing to save",
      },
      saveDialog: {
        title: "Re-rendering Video",
        doneTitle: "Complete",
        errorTitle: "Error",
        message: "Your crop decisions are being applied. This may take a few minutes.",
        done: "Re-render complete. The video will be available shortly.",
        error: "Failed to save or re-render. Please try again.",
        backToContent: "Back to Content",
        close: "Close",
      },
      errors: {
        loadFailed: "Failed to load edit data",
        saveFailed: "Failed to save",
        splitOutOfRange: "Playhead must be inside the selected scene to split",
      },
    },
    overlayTemplates: {
      title: "Overlay Templates",
      subtitle: "Design and manage overlay layer templates for the reframer",
      new: "New Template",
      empty: "No templates yet. Create one to get started.",
      colName: "Name",
      colElements: "Elements",
      colUpdated: "Updated",
      colActions: "Actions",
      actionEdit: "Edit",
      actionDuplicate: "Duplicate",
      actionDelete: "Delete",
      duplicated: "Template duplicated",
      deleted: "Template deleted",
      deleteTitle: "Delete Template",
      deleteConfirm: 'Delete "{name}"? This cannot be undone.',
      cancel: "Cancel",
      deleting: "Deleting…",
      back: "Back",
      namePlaceholder: "Template name (required)",
      unsaved: "Unsaved changes",
      save: "Save",
      saving: "Saving…",
      saved: "Template saved",
      created: "Template created",
      undo: "Undo (Ctrl+Z)",
      redo: "Redo (Ctrl+Shift+Z)",
      untitled: "Untitled Template",
      presets: {
        title: "Choose a starting point",
        blank: "Blank",
        blankDesc: "Empty canvas — build from scratch",
        breakingNews: "Breaking News",
        breakingNewsDesc: "Bold headline bar at the bottom",
        lowerThird: "Lower Third",
        lowerThirdDesc: "Name + title anchored bottom-left",
        watermark: "Watermark",
        watermarkDesc: "Logo + caption in the top-right corner",
        skip: "Skip — start from blank",
      },
      layers: {
        header: "Layers",
        empty: "No layers yet",
        addText: "Add Text",
        addLogo: "Add Logo",
        addGradient: "Add Gradient",
        addShape: "Add Shape",
        typeText: "Text",
        typeLogo: "Logo",
        typeGradient: "Gradient",
        typeShape: "Shape",
      },
      toolbar: {
        centerH: "Center Horizontally",
        centerV: "Center Vertically",
        fillCanvas: "Fill Canvas",
        bringForward: "Bring Forward",
        sendBackward: "Send Backward",
        bringFront: "Bring to Front",
        sendBack: "Send to Back",
        duplicate: "Duplicate",
        delete: "Delete",
      },
      props: {
        position: "Position & Size",
        rotation: "Rotation",
        opacity: "Opacity",
        zOrder: "Z-order",
        selectPrompt: "Select an element to edit its properties.",
        typeProperties: "{type} properties",
      },
      text: {
        header: "Text",
        label: "Label",
        defaultText: "Default Text",
        font: "Font",
        size: "Size (px)",
        weight: "Weight",
        weightBold: "Bold",
        weightNormal: "Normal",
        align: "Align",
        color: "Color",
        shadow: "Shadow",
        shadowOffsetX: "Offset X",
        shadowOffsetY: "Offset Y",
        shadowBlur: "Blur",
        outline: "Outline",
        outlineWidth: "Width",
      },
      logo: {
        header: "Logo (locked to template — only text is dynamic)",
        upload: "Upload Logo",
        replace: "Replace Logo",
        uploading: "Uploading…",
        resetAspect: "Reset to natural aspect ratio",
        resetting: "Resetting…",
      },
      gradient: {
        header: "Gradient",
        direction: "Direction (deg)",
        stops: "Stops — click bar to add, click handle to edit",
        color: "Color",
        alpha: "Alpha",
        remove: "Remove",
      },
      shape: {
        header: "Shape",
        fill: "Fill",
        alpha: "Alpha",
        radius: "Corner Radius (px)",
      },
    },
    overlayPicker: {
      enable: "Add overlay layer",
      selectTemplate: "Select template",
      noTemplates: "No templates yet. Create one first.",
      texts: "Text content",
      timing: "Show overlay",
      timingFull: "Full duration",
      timingFromStart: "First N seconds",
      timingRange: "Range",
      start: "Start (sec)",
      end: "End (sec)",
      required: "All overlay text fields are required",
    },
    seo: {
      title: "RankLens",
      subtitle: "SEO & GEO analysis — {n} signals across technical, content, and AI visibility",
      urlPlaceholder: "Enter an article URL (e.g. https://13tv.co.il/...)",
      analyze: "Analyze",
      analyzing: "Analyzing {n} SEO & GEO signals... this may take up to two minutes",
      emptyTitle: "Paste a URL to get started",
      emptySubtitle: "RankLens checks {n} signals across three pillars to score your article's visibility.",
      pillarTechnical: "SEO Technical",
      pillarContent: "Content Quality",
      pillarGeo: "GEO Signals",
      scores: {
        overallScore: "Overall Score",
        seoScore: "SEO Technical",
        contentScore: "Content Quality",
        geoScore: "GEO Signals",
        filterHint: "Click a pillar to filter findings",
      },
      fixes: {
        priorityTitle: "Priority Fixes",
      },
      checks: {
        checksTitle: "Full Check Report",
        seoCategory: "SEO Technical",
        contentCategory: "Content Quality",
        geoCategory: "GEO Signals",
      },
      reAnalyze: "Re-analyze",
      quickWins: {
        title: "Quick Wins",
      },
      findings: {
        title: "Findings",
        issuesSuffix: "issues",
        failSuffix: "fail",
        warnSuffix: "warn",
        passedSuffix: "passed",
        checksPassed: "checks passed",
        whyLabel: "Why it matters",
        howLabel: "How to fix",
        optimalLabel: "Optimal",
        noIssues: "All checks passed",
        clearFilter: "Clear filter",
        seoCategory: "SEO Technical",
        contentCategory: "Content Quality",
        geoCategory: "GEO Signals",
        performanceCategory: "Performance",
        geoContentGroup: "Enhance with AI",
        geoWebsiteGroup: "Platform-level fixes",
        platformMetadataDisclaimer: "These fixes can't be applied directly inside the CMS. They are generated and embedded as validated metadata in your article bundle — schema markup, structured data, and meta tags — ready to be consumed and rendered by any publishing platform.",
      },
      teaser: {
        title: "Enhance with AI",
        badge: "Beta",
        subtitle: "Rewrite this article to fix content-level issues automatically — entity definitions, answer-first structure, source attributions, and more. Structural fixes are captured as validated metadata in the article bundle.",
        cta: "Enhance with AI",
        ctaLoading: "Generating...",
        ctaSuccess: "Track in Queue",
        disclaimer: "Content-layer fixes are applied directly. Structural fixes — schema markup, meta tags, and structured data — are embedded as validated metadata in the article bundle, ready for your publishing platform.",
        platformDisclaimer: "Platform-level issues — redirects, response headers, robots.txt, Core Web Vitals — are outside the scope of this fix and require changes on your web server or CMS platform.",
        failingIssues: (n) => `${n} issue${n !== 1 ? "s" : ""}`,
        warningIssues: (n) => `${n} warning${n !== 1 ? "s" : ""}`,
        impactPoints: (n) => `Up to ${n} pts recoverable`,
        video: {
          title: "Generate from Video",
          badge: "Beta",
          subtitle: "This is not an article, let's make it one. We'll extract the video content and generate a full article from it.",
          cta: "Generate from Video",
          ctaLoading: "Generating...",
          ctaSuccess: "Track in Queue",
          disclaimer: "Generates a new GEO-optimized article from the embedded video. Original text is replaced.",
          platformDisclaimer: "Platform-level issues — redirects, response headers, robots.txt, Core Web Vitals — are outside the scope of this fix.",
        },
      },
    },
    todaybefore: {
      title: "Today Before",
      subtitle: "What happened on this date — turn a moment in history into an article",
      refresh: "Refresh today",
      refreshing: "Refreshing…",
      yearsAgo: "{n} years ago",
      generate: "Generate article",
      openSource: "Open source",
      openArticle: "Open article",
      empty: "Nothing on record for today",
      emptyHint: "Wikipedia and the Knesset feed had no events for this date.",
      sources: {
        wikipedia: "Wikipedia",
        knesset: "Knesset",
      },
      state: {
        generating: "Generating…",
        generated: "Article ready",
      },
      confirm: {
        title: "Generate an article?",
        message: "A Hebrew article will be written from this event. It takes a few minutes.",
        cancel: "Cancel",
        confirm: "Generate",
      },
      toaster: {
        queued: "Article generation queued",
        alreadyQueued: "This event is already being generated",
        failed: "Failed to queue article generation",
        refreshed: "Today's events refreshed",
        refreshFailed: "Failed to refresh today's events",
      },
    },
    trendscout: {
      title: "TrendScout",
      subtitle: "AI-curated trending stories from around the world",
      tabs: {
        articles: "Articles",
        settings: "Settings",
      },
      status: {
        lastScan: "Last scan:",
        scanNow: "Scan now",
        never: "No scan yet — click to run",
        scanning: "Scanning…",
        scheduler: {
          active: "Active",
          disabled: "Disabled",
        },
      },
      relative: {
        now: "just now",
        secAgo: "s ago",
        minAgo: "min ago",
        hAgo: "h ago",
        dAgo: "d ago",
      },
      filters: {
        searchPlaceholder: "Search by title…",
        countries: "Countries",
        countriesEmpty: "No countries available",
        clear: "Clear",
        rtlToggle: "RTL",
        all: "All",
        written: "Written",
        writtenTip: "Show only stories that already became articles",
        sortHot: "Hottest",
        sortNew: "Newest",
      },
      card: {
        write: "Write article",
        writing: "Writing…",
        written: "Article ready",
        taken: "Already written",
        failed: "Failed",
        retry: "Try again",
        failedHint: "The last attempt failed. Nothing was written.",
        open: "Open draft",
        openSource: "Read the original",
        scoreTip: "Click appeal: {n} out of 10",
        writingHint: "Running in the background — this card updates on its own.",
      },
      table: {
        title: "Title",
        description: "Description",
        source: "Source",
        tags: "Tags",
        published: "Published",
        actions: "Actions",
        empty: "No trending stories yet.",
        noMatches: "No stories match your search or filters.",
        noMatchesHint: "Try a different title or clear the filters.",
        nextScanIn: "Next scan in {n} minutes.",
        pageOf: "Page {n} of {m}",
        totalLabel: "{n} total",
        prevPage: "Previous page",
        nextPage: "Next page",
      },
      coverage: {
        heading: "Coverage",
        help: "Pick the countries and topics TrendScout should monitor.",
        countries: "Countries",
        topics: "Topics",
        selectAll: "Select all",
        clear: "Clear",
        summary: "{c}/{tc} countries · {t}/{tt} topics selected",
        summaryCountries: "{c} of {tc} selected",
        save: "Save changes",
        saving: "Saving…",
        discard: "Discard",
        browseCountries: "Browse all countries ({n})",
        modalTitle: "Select countries",
        searchPlaceholder: "Search by name or code…",
        noResults: "No countries match that search.",
        noCountriesSelected: "No countries selected yet — browse to add some.",
        done: "Done",
      },
      confirmGenerate: {
        title: "Generate this article?",
        message:
          "TrendScout will research the story and write a full Hebrew article. It runs in the background for a few minutes — this card tracks it and turns into a link to the draft.",
        cancel: "Cancel",
        confirm: "Generate",
      },
      topicNames: {
        WORLD: "World",
        // Google's NATION bucket is each source country's own domestic news —
        // not Israeli news. "Home Desk" reads that way from the source's side.
        NATION: "Home Desk",
        BUSINESS: "Money",
        TECHNOLOGY: "Tech",
        ENTERTAINMENT: "Buzz",
        SPORTS: "Sports",
        SCIENCE: "Science",
        // Not "Lifestyle": Google's HEALTH bucket is medicine, studies and
        // outbreaks — an editor opening it for food/fashion/travel finds none.
        HEALTH: "Health & Living",
      },
      duration: { ms: "ms", s: "s", m: "m" },
      toaster: {
        refreshTriggered: "Scan queued — refreshing shortly.",
        refreshFailed: "Could not trigger a scan.",
        fetchFailed: "Failed to load TrendScout data.",
        saveSuccess: "Coverage updated.",
        saveError: "Could not save coverage.",
        generationQueued: "Writing started — the card updates itself when the draft is ready.",
        generationFailed: "Could not queue article generation.",
        generationAlreadyQueued: "Article already queued for generation.",
      },
    },
    FourOFour: {
      title: "404",
      description: "We were unable to find the page you were looking for.",
    },
    FourOOne: {
      title: "401",
      description: "Sorry, your account does not have the required access.",
    },
    users: {
      title: "Users",
      subtitle: "Manage users, roles, and access",
      new: { long: "New User", short: "New" },
      search: { placeholder: "Search by name or email" },
      columns: {
        name: "Name",
        email: "Email",
        role: "Role",
        group: "Group",
        active: "Active",
        actions: "Actions",
      },
      roles: { admin: "Admin", user: "User" },
      status: { active: "Active", inactive: "Inactive" },
      actions: {
        edit: "Edit user",
        resetPassword: "Reset password",
        toggleActive: "Toggle active",
      },
      activeTooltip: {
        on: "Click to deactivate",
        off: "Click to activate",
      },
      groupPlaceholder: "(no group)",
      dialogs: {
        create: {
          title: "New user",
          name: "Name",
          email: "Email",
          password: "Initial password",
          role: "Role",
          group: "Group (optional)",
          active: "Active",
          cancel: "Cancel",
          create: "Create",
        },
        edit: {
          title: "Edit user",
          name: "Name",
          email: "Email",
          role: "Role",
          group: "Group",
          active: "Active",
          cancel: "Cancel",
          save: "Save",
        },
        resetPassword: {
          title: "Reset password",
          message: "Set a new password for this user.",
          newPassword: "New password",
          cancel: "Cancel",
          confirm: "Reset",
        },
      },
      toaster: {
        created: "User created",
        updated: "User updated",
        passwordReset: "Password reset",
        activated: "User activated",
        deactivated: "User deactivated",
        loadFailed: "Failed to load users",
        error: "Action failed",
      },
    },
    statistics: {
      title: "Statistics",
      subtitle: "Content production analytics by source, month, and user",
      filters: {
        from: "From",
        to: "To",
        user: "User",
        source: "Source",
        apply: "Apply",
        clear: "Clear",
        allUsers: "All users",
        allSources: "All sources",
        excludeAdmins: "Exclude admins",
      },
      kpis: {
        total: "Total content",
        downloaded: "Downloaded",
        published: "Published",
        downloadedNotPublished: "Downloaded but not published",
      },
      charts: {
        byMonth: "By month",
        bySource: "By source",
        empty: "No data for these filters",
      },
      table: {
        title: "By user",
        user: "Name",
        email: "Email",
        downloads: "Downloads",
        publishes: "Publishes",
        byArticle: "By article",
        article: "Article",
        lastDownload: "Last download",
        actions: "Actions",
        viewHistory: "Download history",
      },
      sources: {
        TEXT: "Text",
        VIDEO: "Video",
        MANUAL: "Manual",
        REFRAMER: "Reframer",
        URL_REWRITER: "URL Rewriter",
      },
      downloadsDialog: {
        title: "Download history",
        empty: "No downloads yet",
        close: "Close",
        columns: {
          user: "User",
          email: "Email",
          date: "Date",
        },
      },
      toaster: {
        loadFailed: "Failed to load statistics",
      },
    },
    profile: {
      title: "My Profile",
      subtitle: "Manage your account",
      fields: {
        name: "Name",
        email: "Email",
        role: "Role",
        group: "Group",
      },
      save: "Save",
      cancel: "Cancel",
      close: "Close",
      changePassword: {
        title: "Change password",
        current: "Current password",
        next: "New password",
        confirm: "Confirm",
      },
      toaster: {
        saved: "Profile updated",
        passwordChanged: "Password changed",
        error: "Action failed",
      },
    },
  },
  layout: {
    sidebar: {
      settings: "Settings",
      quickActions: "Quick Actions",
      siteName: "Reshet 13",
      profile: ["Change Theme", "עבור לעברית", "My Profile", "Log out"],
    },
  },
  components: {
    uploadArea: {
      subtitle: "Drag and drop files here, or click to select files",
    },
    markdownEditor: {},
    mediaEditor: {
      image: {
        title: "Edit Image",
        close: "Close",
        saving: "Saving...",
        uploadFailed: "Failed to upload edited image",
        filerobot: {
          name: "Name",
          save: "Save",
          saveAs: "Save as",
          back: "Back",
          loading: "Loading...",
          resetOperations: "Reset all",
          changesLoseWarningHasChanges: "You have unsaved changes.",
          discardChanges: "Discard",
          continue: "Continue",
          adjustTab: "Adjust",
          finetuneTab: "Finetune",
          filtersTab: "Filters",
          watermarkTab: "Watermark",
          annotateTab: "Annotate",
          resizeTab: "Resize",
          cropTool: "Crop",
          rotateTool: "Rotate",
          flipTool: "Flip",
          original: "Original",
          custom: "Custom",
          landscape: "Landscape",
          portrait: "Portrait",
          square: "Square",
          brightness: "Brightness",
          contrast: "Contrast",
          saturation: "Saturation",
          exposure: "Exposure",
          hue: "Hue",
          warmth: "Warmth",
          lighten: "Lighten",
          sharpness: "Sharpness",
          blur: "Blur",
          gamma: "Gamma",
          addTextPlaceholder: "Add Text",
          width: "Width",
          height: "Height",
          aspectRatioLockTip: "Lock/Unlock ratio",
          undo: "Undo",
          redo: "Redo",
          download: "Download",
          cancel: "Cancel",
          apply: "Apply",
          addWatermarkTitle: "Add Watermark",
          watermarkLink: "Link",
          addWatermarkAsText: "Add as text",
          addWatermarkAsImage: "Add as image",
        },
      },
      video: {
        title: "Edit Video",
        duration: "Duration",
        start: "Start",
        end: "End",
        speed: "Speed",
        mute: "Mute audio",
        cancel: "Cancel",
        save: "Save",
        processing: "Processing...",
        errorPrefix: "Could not edit video",
        invalidRange: "Start must be before end.",
      },
      toolbar: {
        editImage: "Edit image",
        editVideo: "Edit video",
        imageUrlPrompt: "Image URL:",
        videoUrlPrompt: "Video URL:",
      },
      confirmDelete: {
        title: "Remove from editor?",
        imageBody:
          "This removes the image from the document only. The file remains in storage and can be reused.",
        videoBody:
          "This removes the video from the document only. The file remains in storage and can be reused.",
        cancel: "Cancel",
        confirm: "Remove",
      },
      contextMenu: {
        editImage: "Edit image",
        editVideo: "Edit video",
        deleteImage: "Delete image",
        deleteVideo: "Delete video",
        copyUrl: "Copy URL",
        cut: "Cut",
        copy: "Copy",
        paste: "Paste",
        undo: "Undo",
        redo: "Redo",
        selectAll: "Select all",
      },
    },
    gallery: {
      undefined: "Unkown Scale",
      copy: "Image URL copied to clipboard",
      empty: "No images to display",
    },
    table: {
      empty: "No data to display",
      copyCell: "Cell content copied to clipboard",
    },
  },
};

export default en;
